import { useMemo, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { KpiStrip, MetricCard, Panel, SectionHeader } from '@/components/erp/primitives'
import { ErrorStrip, Forbidden, LoadingBlock, Note } from '@/shared/ui/states'
import { useCan } from '@/shared/auth/session'
import { useLoad } from '@/features/administration/hooks'
import { explain } from '@/shared/api/problem'
import { fmtDateTime } from '@/shared/lib/format'
import type { SurveyEstimateDto } from '@/shared/api/types'
import type { Estimator } from './api'
import type { Finding } from '@/shared/ui/council'
import { getEstimates, getEstimator, listPolls, listWeightRuns, startWeightRun } from './api'
import { AskPanel, CouncilBoard } from '@/shared/ui/council'
import { advise, ask } from './analysis/council'
import { LeadPlot } from './analysis/LeadPlot'
import { PollSelect } from './PollSelect'
import { Pill, baseStatement, count, countish, num, pct, pp, weightMethodLabel }
  from './vocab'

/**
 * Published figures — and the sentences where there are none.
 *
 * ## This screen is the whole plan in one view
 *
 * Every number on it arrives from the server with its base, its design effect and
 * its margin attached. Nothing here computes a percentage: there is no arithmetic
 * on raw responses anywhere in this feature, which is what guarantees the bar and
 * the table beside it agree.
 *
 * **A suppressed cell renders its sentence, not a number.** Not a zero, not a
 * dash with a tooltip — the sentence the server composed, which carries the base
 * and the threshold it actually used. PollSphere, given forty interviews, reports
 * a margin of error of ±15.5%, a design effect of 1.5 (a literal, commented
 * "typical for quota-based field samples") and a confidence interval computed
 * around the constant 0.5.
 *
 * ## The weight-run badge is not decoration
 *
 * "Raked · deff 1.42 · effective n 848" is the provenance of every figure below
 * it. A run that did not converge suppresses everything, and the badge says so.
 */
export function ResultsPage() {
  const canAnalyse = useCan('survey:analyse')
  const [params, setParams] = useSearchParams()
  const pollId = params.get('poll')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  // The council reads ONE question — a lead is a statement about one ballot, and a finding
  // averaged across questions would be a finding about nothing.
  const questionId = params.get('q')
  const tab = params.get('tab') === 'council' ? 'council' : 'estimates'

  const polls = useLoad(() => listPolls({}, 0, 100), [])
  const estimates = useLoad(
    () => (pollId ? getEstimates(pollId) : Promise.resolve(null)), [pollId])
  const runs = useLoad(
    () => (pollId ? listWeightRuns(pollId) : Promise.resolve([])), [pollId])
  const estimator = useLoad(
    () => (pollId ? getEstimator(pollId, questionId ?? undefined) : Promise.resolve(null)),
    [pollId, questionId])

  const findings = useMemo(
    () => (estimator.data ? advise(estimator.data) : []), [estimator.data])

  if (!canAnalyse) return <Forbidden what="poll results" />

  const setParam = (key: string, value: string | null) => {
    const next = new URLSearchParams(params)
    if (value) next.set(key, value)
    else next.delete(key)
    // Changing the poll must drop the question: a question id from another poll resolves to
    // nothing, and the console would show an empty board rather than the new poll's default.
    if (key === 'poll') next.delete('q')
    setParams(next, { replace: true })
  }
  const setPoll = (id: string) => setParam('poll', id || null)

  async function runWeighting() {
    if (!pollId) return
    setBusy(true)
    setError(null)
    try {
      await startWeightRun(pollId)
      runs.reload()
    } catch (e) {
      setError(explain(e))
    } finally {
      setBusy(false)
    }
  }

  const set = estimates.data
  const byQuestion = new Map<string, SurveyEstimateDto[]>()
  for (const row of set?.estimates ?? []) {
    const key = row.questionCode ?? row.questionId
    byQuestion.set(key, [...(byQuestion.get(key) ?? []), row])
  }
  const latestRun = (runs.data ?? [])[0]

  return (
    <div className="space-y-4">
      <SectionHeader
        title="Results"
        description="Weighted estimates, each with its base, its margin and its provenance."
        right={(
          <div className="flex items-center gap-2">
            <PollSelect polls={polls.data?.content ?? []} value={pollId} onChange={setPoll} />
            {pollId && (
              <button type="button" className="erp-btn-secondary h-9 text-erp-sm"
                disabled={busy} onClick={runWeighting}>
                {busy ? 'Queueing…' : 'Run weighting'}
              </button>
            )}
          </div>
        )}
      />

      <ErrorStrip message={error ?? estimates.error ?? estimator.error} />

      {pollId && (
        <div className="flex flex-wrap gap-1 border-b border-border" role="tablist">
          {([
            { id: 'estimates', label: 'Estimates', hint: 'Every question, with its base and margin' },
            { id: 'council', label: 'AI Council', hint: 'What this number can bear, read as decisions' },
          ] as const).map((t) => (
            <button key={t.id} role="tab" aria-selected={tab === t.id} title={t.hint}
                    onClick={() => setParam('tab', t.id === 'estimates' ? null : t.id)}
                    className={`px-3 py-2 text-erp-sm rounded-t-md border-b-2 -mb-px transition-colors ${
                      tab === t.id
                        ? 'border-primary text-foreground font-medium'
                        : 'border-transparent text-muted-foreground hover:text-foreground hover:bg-muted/50'}`}>
              {t.label}
              {t.id === 'council' && findings.some((f) => f.severity === 'critical') && (
                <span className="ml-1.5 inline-flex items-center justify-center rounded-full bg-rose-600 text-white text-erp-xs px-1.5 min-w-[18px] h-[18px] tabular-nums">
                  {findings.filter((f) => f.severity === 'critical').length}
                </span>
              )}
            </button>
          ))}
        </div>
      )}

      {pollId && tab === 'council' && (
        <CouncilTab
          estimator={estimator.data}
          loading={estimator.loading}
          findings={findings}
          pollId={pollId}
          questionId={questionId}
          onQuestion={(id) => setParam('q', id)}
        />
      )}

      {!pollId ? (
        <Note tone="info">Pick a poll.</Note>
      ) : tab === 'council' ? null
        : estimates.loading && !set ? <LoadingBlock label="Loading estimates…" /> : set && (
        <>
          {set.unavailable ? (
            <Note tone="info">{set.unavailable}</Note>
          ) : (
            <>
              <KpiStrip>
                <MetricCard label="Method" value={weightMethodLabel(set.methodCode)}
                  sub={set.converged === false ? 'did not converge' : 'converged'}
                  accent={set.converged === false} />
                <MetricCard label="Design effect" value={num(set.deff)}
                  sub="measured from the weights, never assumed" />
                <MetricCard label="Effective n" value={countish(set.effectiveN)}
                  sub={`from ${count(set.baseN)} interviews`} />
                <MetricCard label="Questions" value={count(byQuestion.size)} sub="with estimates" />
                <MetricCard label="Suppressed"
                  value={count(set.estimates.filter((e) => e.suppressed).length)}
                  sub="cells withheld, with a reason" />
              </KpiStrip>

              {set.converged === false && (
                <Note tone="bad">
                  This weight run did not converge, so every estimate in it is suppressed. That
                  is deliberate: shipping the last iterate of a raking that did not settle is
                  shipping a number nobody can defend.
                </Note>
              )}
            </>
          )}

          {latestRun && (
            <Panel title="Provenance" description="Which run produced the figures below.">
              <div className="flex flex-wrap items-center gap-2 text-erp-sm">
                <Pill>{weightMethodLabel(latestRun.methodCode)}</Pill>
                <Pill>{`deff ${num(latestRun.deff)}`}</Pill>
                <Pill>{`effective n ${countish(latestRun.effectiveN)}`}</Pill>
                <Pill>{`${latestRun.iterations} iteration${latestRun.iterations === 1 ? '' : 's'}`}</Pill>
                {latestRun.trimmedCount > 0 && (
                  <Pill>{`${latestRun.trimmedCount} weight${latestRun.trimmedCount === 1 ? '' : 's'} trimmed`}</Pill>
                )}
                <span className="text-muted-foreground">
                  {latestRun.finishedAt ? fmtDateTime(latestRun.finishedAt) : latestRun.stateCode}
                </span>
              </div>
              {latestRun.targets.length > 0 && (
                <div className="mt-3 grid gap-1.5">
                  <p className="text-erp-xs uppercase tracking-wide text-muted-foreground">
                    Raked to
                  </p>
                  {latestRun.targets.map((t) => (
                    <div key={`${t.dimensionCode}-${t.valueCode}`}
                      className="flex items-center gap-2 text-erp-xs">
                      <span className="w-40 truncate text-muted-foreground">
                        {t.dimensionCode} · {t.valueCode}
                      </span>
                      <span className="tabular-nums">{pct(t.targetShare)} target</span>
                      <span className="tabular-nums text-muted-foreground">
                        {pct(t.achievedShare)} achieved
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </Panel>
          )}

          {[...byQuestion.entries()].map(([code, rows]) => (
            <QuestionResult key={code} code={code} rows={rows} />
          ))}
        </>
      )}
    </div>
  )
}

/**
 * One question's distribution.
 *
 * The bars are the small ERP kind: a horizontal fill, a tabular-numeral share and
 * an MoE whisker rendered as text rather than a chart annotation nobody can read
 * at this density.
 */
function QuestionResult({ code, rows }: { code: string; rows: SurveyEstimateDto[] }) {
  const published = rows.filter((r) => !r.suppressed)
  const suppressed = rows.filter((r) => r.suppressed)
  const baseN = rows[0]?.baseN ?? 0
  const baseCode = rows[0]?.baseCode ?? 'SUBSTANTIVE'

  return (
    <Panel title={code} description={baseStatement(baseN, baseCode)}>
      {published.length === 0 ? (
        // THE SENTENCE, IN PLACE OF THE NUMBER. Composed server-side so every
        // client says the same thing and the figures in it are the ones the
        // suppression actually used.
        <Note tone="info">
          {suppressed[0]?.suppressedSentence ?? 'No figure is published for this question.'}
        </Note>
      ) : (
        <div className="space-y-2">
          {published
            .slice()
            .sort((a, b) => Number(b.estimate ?? 0) - Number(a.estimate ?? 0))
            .map((row) => (
              <div key={row.id} className="grid grid-cols-[minmax(120px,26%)_1fr_auto] items-center gap-3">
                <span className="truncate text-erp-sm" title={row.optionLabel ?? undefined}>
                  {row.optionLabel ?? row.optionCode ?? '—'}
                </span>
                <span className="h-2 rounded bg-muted overflow-hidden">
                  <span
                    className="block h-full bg-chocolate"
                    style={{ width: `${Math.max(0, Math.min(100, Number(row.estimate ?? 0) * 100))}%` }}
                  />
                </span>
                <span className="tabular-nums text-erp-sm whitespace-nowrap">
                  {pct(row.estimate)}
                  <span className="ml-1.5 text-muted-foreground text-erp-xs">
                    {pp(row.moe95)}
                  </span>
                </span>
              </div>
            ))}
          {suppressed.length > 0 && (
            <p className="text-erp-xs text-muted-foreground pt-1">
              {suppressed.length} cell{suppressed.length === 1 ? '' : 's'} withheld:
              {' '}{suppressed[0]?.suppressedSentence}
            </p>
          )}
        </div>
      )}
    </Panel>
  )
}


/**
 * The council tab — one question, its distribution, and what may be said about it.
 *
 * ## Why the question picker is here and not in the header
 *
 * The Estimates tab is a survey of everything the poll published; the council is a reading
 * of ONE ballot. A lead is a statement about a single question, and a finding averaged over
 * several would be a finding about nothing. Putting the picker inside the tab keeps that
 * distinction visible instead of making it a hidden mode of the page.
 */
/**
 * The poll, written out for the model — served figures only.
 *
 * The module's guarantee is that nothing in the polling client computes a percentage, and it
 * survives here: every number below is read off the estimator payload. It matters more than
 * usual that the GAP margin is passed as served, because a model asked to reason about a lead
 * from two shares and a sample size will reach for the wrong formula — the shares are
 * negatively correlated, and √(se₁²+se₂²) overstates the uncertainty. The instruction below
 * tells it not to recompute.
 */
function pollContext(e: Estimator | null, findings: Finding[]): string {
  if (!e) return 'No poll payload is loaded.'
  const pct = (v: number | null | undefined, d = 1) =>
    v == null ? 'withheld' : `${(v * 100).toFixed(d)}%`
  const pp = (v: number | null | undefined, d = 1) =>
    v == null ? '—' : `±${(v * 100).toFixed(d)} pts`
  const sep = e.separations[0]
  const a = e.attrition

  return [
    'You are advising a Kenyan campaign on what one opinion poll is allowed to support.',
    'Answer in plain language for a candidate, not a statistician. Use ONLY the served figures',
    'below and DO NOT recompute any margin — in particular the margin on the LEAD is already',
    'given and is not the square root of the sum of the two shares\' squared errors.',
    '',
    `POLL: ${e.poll.ref} — ${e.poll.title} (${e.poll.statusCode}, ${e.poll.methodologyCode}),`
      + ` scope ${e.poll.scopeUnitName ?? 'national'}, target ${e.poll.targetSample}.`,
    `QUESTION: ${e.question?.text ?? 'none selected'}`,
    e.design
      ? `DESIGN: ${e.design.baseN} interviews, effective n ${e.design.effectiveN ?? '—'},`
        + ` design effect ${e.design.deff ?? '—'}, weighting ${e.design.methodCode ?? 'none'}`
        + ` (${e.design.converged === false ? 'DID NOT CONVERGE' : 'converged'}).`
      : 'DESIGN: no weight run attached.',
    '',
    'SHARES (option, estimate, margin, 95% interval):',
    ...e.bands.map((b) => (b.suppressed
      ? `- ${b.optionLabel}: WITHHELD — ${b.suppressedSentence ?? 'below the publication threshold'}`
      : `- ${b.optionLabel}: ${pct(b.estimate)} ${pp(b.moe95)}, ${pct(b.ciLower)} to ${pct(b.ciUpper)}`)),
    '',
    sep
      ? `THE LEAD: ${sep.aLabel} over ${sep.bLabel}, gap ${pct(sep.gap)} ${pp(sep.gapMoe95)}.`
        + ` Separated: ${sep.separated}. The two options' own intervals overlap: ${sep.intervalsOverlap}.`
        + ` Probability the lead is genuine: ${pct(sep.leadProbability, 0)}.`
      : 'THE LEAD: no two options can be compared on this question.',
    a
      ? `FIELDWORK: ${a.complete} complete of ${a.eligibleBase} eligible; response rate`
        + ` ${pct(a.responseRate1)}, cooperation ${pct(a.cooperationRate1)}, refusal`
        + ` ${pct(a.refusalRate1)}; ${a.flagged} interviews flagged and excluded.`
      : 'FIELDWORK: no disposition record.',
    e.budget
      ? `PRECISION: realised margin ${pp(e.budget.realisedMoe)} against ${pp(e.budget.simpleRandomMoe)}`
        + ` for a simple random sample of the same size — ${e.budget.lostInterviews} interviews'`
        + ' worth of precision lost to weighting.'
      : '',
    e.subgroups.filter((s) => s.thin).length > 0
      ? `TOO THIN TO REPORT: ${e.subgroups.filter((s) => s.thin)
          .map((s) => `${s.dimensionCode}·${s.valueCode} (n=${s.baseN})`).join(', ')}`
      : 'SUBGROUPS: all reportable.',
    '',
    'WHAT OUR OWN RULES ALREADY FLAGGED (agree, refine or disagree):',
    ...findings.map((f) => `- [${f.severity}] ${f.headline} ${f.evidence}`),
  ].filter(Boolean).join('\n')
}

function CouncilTab({
  estimator, loading, findings, pollId, questionId, onQuestion,
}: {
  estimator: Estimator | null
  loading: boolean
  findings: Finding[]
  pollId: string
  questionId: string | null
  onQuestion: (id: string) => void
}) {
  if (loading && !estimator) return <LoadingBlock label="Reading the poll…" />
  if (!estimator) return <Note tone="info">No estimator payload for this poll.</Note>
  if (estimator.unavailable) return <Note tone="info">{estimator.unavailable}</Note>

  const q = estimator.question
  return (
    <div className="space-y-4">
      <Panel
        title={q ? q.text : 'No question selected'}
        description={q ? `${q.code} · ${q.optionCount} options` : undefined}
        right={estimator.questions.length > 1 ? (
          <select
            value={questionId ?? q?.id ?? ''}
            onChange={(e) => onQuestion(e.target.value)}
            className="rounded-md border border-border bg-background px-2 py-1.5 text-erp-sm"
            aria-label="Question to read"
          >
            {estimator.questions.map((it) => (
              <option key={it.id} value={it.id}>{it.code} — {it.text}</option>
            ))}
          </select>
        ) : undefined}
      >
        <LeadPlot bands={estimator.bands} separation={estimator.separations[0] ?? null} />
      </Panel>

      <div className="grid gap-4 lg:grid-cols-3">
        <div className="lg:col-span-2">
          <CouncilBoard
            findings={findings}
            title="What this poll can bear"
            description={`${findings.length} ${findings.length === 1 ? 'finding' : 'findings'} over the served estimates, the fieldwork and the weighting. Nothing here is recomputed.`}
            stakeUnit="interview"
            emptyMessage="Nothing crosses a threshold on this question. The poll is unremarkable, which for a poll is good news."
          />
        </div>
        <AskPanel
          storageKey={`polling-council-history:${pollId}`}
          description="Plain language. The poll's served figures go with the question, so the answer is about this ballot."
          placeholder="Is my lead real?"
          scopeNote="It sees this question's estimates, its fieldwork and its weighting — not the raw interviews."
          context={() => pollContext(estimator, findings)}
          suggestions={[
            'Is my lead real?', 'Can I trust this poll?', 'What can I say about young voters?',
            'How many more interviews do I need?', 'Is the sample representative?',
          ]}
          onAsk={(question) => ask(question, findings)}
        />
      </div>
    </div>
  )
}
